import React from 'react';
import { useDoctorSpecialties } from '../hooks/useDoctors';

interface DoctorSpecialtiesDisplayProps {
  doctorId: string;
  variant?: 'default' | 'light';
}

const DoctorSpecialtiesDisplay: React.FC<DoctorSpecialtiesDisplayProps> = ({ 
  doctorId, 
  variant = 'default' 
}) => {
  const { doctorSpecialties, loading, error } = useDoctorSpecialties(doctorId);

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className={`h-6 rounded ${variant === 'light' ? 'bg-blue-200' : 'bg-gray-200'} w-40`}></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`text-sm ${variant === 'light' ? 'text-blue-100' : 'text-red-500'}`}>
        <span className="flex items-center">
          ⚠️ Fehler beim Laden der Fachgebiete
          {error.includes('503') || error.includes('timeout') ? ' (Server temporär nicht verfügbar)' : ''}
        </span>
      </div>
    );
  }

  if (!doctorSpecialties || doctorSpecialties.length === 0) {
    return (
      <div className={`text-sm italic ${variant === 'light' ? 'text-blue-100' : 'text-gray-500'}`}>
        Keine Fachgebiete angegeben
      </div>
    );
  }

  // Find primary specialty
  const primarySpecialty = doctorSpecialties.find(ds => ds.is_primary);
  const otherSpecialties = doctorSpecialties.filter(ds => !ds.is_primary);
  
  // If no primary is set, use the first one as primary
  const displayPrimary = primarySpecialty || doctorSpecialties[0];
  const displayOthers = primarySpecialty ? otherSpecialties : doctorSpecialties.slice(1);

  return (
    <div className="space-y-2">
      {/* Primäres Fachgebiet prominent */}
      <div className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium ${
        variant === 'light' 
          ? 'bg-white bg-opacity-20 text-white' 
          : 'bg-blue-100 text-blue-800'
      }`}>
        <span className="w-2 h-2 bg-current rounded-full mr-2 opacity-60"></span>
        {displayPrimary.specialty?.name}
      </div>
      
      {/* Weitere Fachgebiete als kleinere Tags */}
      {displayOthers.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {displayOthers.slice(0, 2).map((specialty, index) => (
            <span 
              key={index}
              className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${
                variant === 'light' 
                  ? 'bg-white bg-opacity-10 text-blue-100' 
                  : 'bg-gray-100 text-gray-700'
              }`}
            >
              {specialty.specialty?.name}
            </span>
          ))}
          {displayOthers.length > 2 && (
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${
              variant === 'light' 
                ? 'bg-white bg-opacity-10 text-blue-200' 
                : 'bg-gray-100 text-gray-500'
            }`}>
              +{displayOthers.length - 2} weitere
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default DoctorSpecialtiesDisplay;